import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import "./ClassDetail.css";

function ClassDetail() {
  const { id } = useParams();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [isClassSaved, setIsClassSaved] = useState(false);
  const [savingClass, setSavingClass] = useState(false);

  useEffect(() => {
    // Check authentication state
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && classData) {
      checkIfClassIsSaved();
    }
  }, [user, classData]);

  useEffect(() => {
    fetchClass();
  }, [id]);

  const fetchClass = async () => {
    try {
      setLoading(true);
      setError(null);

      const classRef = doc(db, "classes", id);
      const classSnap = await getDoc(classRef);

      if (classSnap.exists()) {
        setClassData({
          id: classSnap.id,
          ...classSnap.data(),
        });
      } else {
        setError("La clase no fue encontrada.");
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching class:", err);
      setError("Error al cargar la información de la clase.");
      setLoading(false);
    }
  };

  const checkIfClassIsSaved = async () => {
    if (!user || !classData) return;

    try {
      const savedClassesRef = collection(db, "userSavedClasses");
      const q = query(
        savedClassesRef,
        where("userId", "==", user.uid),
        where("classId", "==", classData.id),
      );

      const querySnapshot = await getDocs(q);
      setIsClassSaved(!querySnapshot.empty);
    } catch (err) {
      console.error("Error checking saved class:", err);
    }
  };

  // In ClassDetail.js - Update handleSaveClass function
  const handleSaveClass = async () => {
    if (!user) {
      window.location.href = "/login";
      return;
    }

    try {
      setSavingClass(true);

      if (isClassSaved) {
        // Remove from saved classes
        const savedClassesRef = collection(db, "userSavedClasses");
        const q = query(
          savedClassesRef,
          where("userId", "==", user.uid),
          where("classId", "==", classData.id),
        );

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(async (doc) => {
          await deleteDoc(doc.ref);
        });

        setIsClassSaved(false);
      } else {
        // Add to saved classes - NOW INCLUDING USER EMAIL
        const savedClassRef = doc(collection(db, "userSavedClasses"));
        await setDoc(savedClassRef, {
          userId: user.uid,
          userEmail: user.email, // ADD THIS LINE
          classId: classData.id,
          className: classData.title,
          classImage: classData.image || classData.gallery?.[0] || "",
          classSchedule: classData.schedule,
          classPrice: classData.price || "Consultar",
          savedAt: new Date().toISOString(),
        });

        setIsClassSaved(true);
      }
    } catch (err) {
      console.error("Error saving class:", err);
      alert("Error al guardar la clase. Por favor, intenta de nuevo.");
    } finally {
      setSavingClass(false);
    }
  };

  // Lightbox functions (keep existing)
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    if (!classData?.gallery) return;
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? classData.gallery.length - 1 : prevIndex - 1,
    );
  };

  const goToNext = (e) => {
    e.stopPropagation();
    if (!classData?.gallery) return;
    setCurrentImageIndex((prevIndex) =>
      prevIndex === classData.gallery.length - 1 ? 0 : prevIndex + 1,
    );
  };

  // Handle keyboard navigation (keep existing)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen || !classData?.gallery) return;

      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;
        case "ArrowLeft":
          goToPrevious({ stopPropagation: () => {} });
          break;
        case "ArrowRight":
          goToNext({ stopPropagation: () => {} });
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, classData?.gallery]);

  // Get CSS class for header background
  const getImageClass = () => {
    if (classData?.image && classData.image.trim() !== "") {
      return classData.image.toLowerCase();
    }

    if (classData?.title) {
      const title = classData.title.toLowerCase();
      if (
        title.includes("danza") ||
        title.includes("baile") ||
        title.includes("dance")
      ) {
        return "dance";
      }
      if (title.includes("pintura") || title.includes("paint")) {
        return "painting";
      }
      if (title.includes("yoga") || title.includes("meditación")) {
        return "yoga";
      }
      if (title.includes("foto") || title.includes("photo")) {
        return "photography";
      }
      if (title.includes("escritura") || title.includes("writing")) {
        return "writing";
      }
      if (title.includes("cerámica") || title.includes("ceramic")) {
        return "ceramics";
      }
    }

    return "default";
  };

  if (loading) {
    return (
      <div className="class-detail-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando información de la clase...</p>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="class-detail-page">
        <div className="error-state">
          <p>{error || "La clase no existe."}</p>
          <Link to="/classes" className="back-link">
            ← Volver a Clases
          </Link>
        </div>
      </div>
    );
  }

  // Ensure gallery is an array
  const gallery = Array.isArray(classData.gallery) ? classData.gallery : [];

  return (
    <div className="class-detail-page">
      {/* Lightbox Modal */}
      {lightboxOpen && gallery.length > 0 && (
        <div className="lightbox" onClick={closeLightbox}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>

            <div className="lightbox-image-container">
              <img
                src={gallery[currentImageIndex]}
                alt={`${classData.title} - Imagen ${currentImageIndex + 1}`}
                className="lightbox-image"
              />

              <button className="lightbox-nav prev" onClick={goToPrevious}>
                ‹
              </button>
              <button className="lightbox-nav next" onClick={goToNext}>
                ›
              </button>
            </div>

            <div className="lightbox-caption">
              <p>
                {classData.title} - Imagen {currentImageIndex + 1} de{" "}
                {gallery.length}
              </p>
            </div>

            {gallery.length > 1 && (
              <div className="lightbox-thumbnails">
                {gallery.map((photo, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? "active" : ""}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={photo} alt={`Miniatura ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header with background */}
      <div className={`detail-header ${getImageClass()}`}>
        <div className="header-overlay">
          <div className="container">
            <Link to="/classes" className="back-link">
              ← Volver a Clases
            </Link>
            <h1>{classData.title}</h1>
            <p className="class-schedule">{classData.schedule}</p>
          </div>
        </div>
      </div>

      <div className="detail-content">
        <div className="container">
          <div className="detail-grid">
            {/* Main Info */}
            <div className="main-info">
              <div className="info-section">
                <h2>Acerca del curso</h2>
                <p>
                  {classData.description || "No hay descripción disponible."}
                </p>
              </div>

              {/* Photo Gallery */}
              {gallery.length > 0 && (
                <div className="gallery-section">
                  <h2>Galería</h2>
                  <div className="gallery-grid">
                    {gallery.map((photo, index) => (
                      <div
                        key={index}
                        className="gallery-item"
                        onClick={() => openLightbox(index)}
                      >
                        <img
                          src={photo}
                          alt={`${classData.title} - Imagen ${index + 1}`}
                          loading="lazy"
                        />
                        <div className="gallery-overlay">
                          <span className="zoom-icon">🔍</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="sidebar">
              {/* Add Save Class Button */}
              <div className="info-card action-card">
                <h3>Reservar Clase</h3>
                <p className="action-description">
                  {user
                    ? "Guarda esta clase en tu perfil para acceder rápidamente."
                    : "Inicia sesión para guardar clases y recibir actualizaciones."}
                </p>

                <button
                  onClick={handleSaveClass}
                  disabled={savingClass}
                  className={`save-class-btn ${isClassSaved ? "saved" : ""}`}
                >
                  {savingClass ? (
                    <>
                      <span className="spinner-small"></span>
                      Procesando...
                    </>
                  ) : user ? (
                    <>
                      {isClassSaved ? (
                        <>
                          <span className="icon">✓</span>
                          Clase Guardada
                        </>
                      ) : (
                        <>
                          <span className="icon">+</span>
                          Guardar Clase
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="icon">→</span>
                      Iniciar Sesión para Guardar
                    </>
                  )}
                </button>
              </div>

              <div className="info-card">
                <h3>Detalles del curso</h3>
                <div className="detail-item">
                  <span className="detail-label">Horario:</span>
                  <span className="detail-value">
                    {classData.schedule || "Por confirmar"}
                  </span>
                </div>
                {classData.duration && (
                  <div className="detail-item">
                    <span className="detail-label">Duración:</span>
                    <span className="detail-value">{classData.duration}</span>
                  </div>
                )}
                {classData.level && (
                  <div className="detail-item">
                    <span className="detail-label">Nivel:</span>
                    <span className="detail-value">{classData.level}</span>
                  </div>
                )}
                {classData.price && (
                  <div className="detail-item">
                    <span className="detail-label">Precio:</span>
                    <span className="detail-value">{classData.price}</span>
                  </div>
                )}
                {classData.whatToBring && (
                  <div className="detail-item">
                    <span className="detail-label">Sugerencias:</span>
                    <span className="detail-value">
                      {classData.whatToBring}
                    </span>
                  </div>
                )}
              </div>

              {(classData.instructor || classData.instructorImage) && (
                <div className="info-card instructor-card">
                  <h3>Instructor{!classData.instructor ? "es" : ""}</h3>
                  <div className="instructor-info">
                    {classData.instructorImage && (
                      <div className="instructor-image">
                        <img
                          src={classData.instructorImage}
                          alt={classData.instructor || "Instructor"}
                        />
                      </div>
                    )}
                    <div className="instructor-details">
                      {classData.instructor && <h4>{classData.instructor}</h4>}
                      {classData.instructorBio && (
                        <p>{classData.instructorBio}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="info-card contact-card">
                <h3>Contacto</h3>
                <div className="contact-info">
                  <p>Para más información o inscripciones:</p>
                  <div className="contact-methods">
                    <div className="contact-method">
                      <span className="contact-icon">📧</span>
                      <span>info@espacioparadigmas.com</span>
                    </div>
                    <div className="contact-method">
                      <span className="contact-icon">📱</span>
                      <span>+54 11 1234 5678</span>
                    </div>
                    <div className="contact-method">
                      <span className="contact-icon">📍</span>
                      <span>Espacio Paradigmas, Buenos Aires</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClassDetail;
